import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { useFeatureGate } from '@statsig/expo-bindings';
import { Tabs } from 'expo-router';
import * as React from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BookOpenIcon,
    ChartLineIcon,
    CircleDollarSignIcon,
    HouseIcon,
    Icon,
    isWeb,
    ListChecksIcon,
    MoneyMapIcon,
    useMedia,
    useThemeColors,
} from 'smoothie';

import Screens from '../../../common/types/Screens';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { useRoadmapFeature } from '../../../roadmap/hooks/useRoadmapFeature';
import {
    SIDE_NAV_WIDTH_LG,
    SIDE_NAV_WIDTH_MD,
    SIDE_NAV_WIDTH_SM,
    SideNavBar,
} from './SideNavBar';

export function SideNavHandler(
    sideNavBreakpoint: boolean,
): ((props: BottomTabBarProps) => React.ReactNode) | undefined {
    if (sideNavBreakpoint) {
        return (props) => <SideNavBar {...props} />;
    }

    return undefined;
}

interface TabBarIconProps {
    color: string;
    focused: boolean;
}

const homeTabProps = {
    name: Screens.Tabs.Home,
    options: {
        title: 'Home',
        tabBarShowLabel: false,
        tabBarIcon: ({ color }: TabBarIconProps) => (
            <Icon icon={HouseIcon} stringColor={color} />
        ),
    },
};

const bankingTabProps = {
    name: Screens.Tabs.Banking,
    options: {
        title: 'Banking',
        tabBarShowLabel: false,
        tabBarIcon: ({ color }: TabBarIconProps) => (
            <Icon icon={CircleDollarSignIcon} stringColor={color} />
        ),
    },
};

const guidanceTabProps = {
    name: Screens.Tabs.MyGuide,
    options: {
        title: 'Guidance',
        tabBarShowLabel: false,
        tabBarIcon: ({ color }: TabBarIconProps) => (
            <Icon icon={BookOpenIcon} stringColor={color} />
        ),
    },
};

const roadmapTabProps = (
    disableRoadmapFeatures: boolean,
    roadmapTitle: string,
) => ({
    name: Screens.Tabs.Roadmap,
    options: {
        title: roadmapTitle,
        tabBarShowLabel: false,
        tabBarIcon: ({ color }: TabBarIconProps) =>
            disableRoadmapFeatures ? (
                <Icon icon={ListChecksIcon} stringColor={color} />
            ) : (
                <Icon icon={MoneyMapIcon} stringColor={color} />
            ),
    },
});

const imTabProps = (hide: boolean) => ({
    name: Screens.Tabs.Investment,
    options: {
        title: 'Investing',
        tabBarShowLabel: false,
        tabBarIcon: ({ color }: TabBarIconProps) => (
            <Icon icon={ChartLineIcon} stringColor={color} />
        ),
        ...(hide && { href: null }),
    },
});

const TabsComponent = ({ children }: { children: React.ReactNode }) => {
    const { getColor } = useThemeColors();
    const insets = useSafeAreaInsets();
    const breakpoints = useMedia();
    const sideNavBreakpoint = breakpoints.sm;

    let homeContentOffset = 0;

    if (breakpoints.lg) {
        homeContentOffset = SIDE_NAV_WIDTH_LG;
    } else if (breakpoints.md) {
        homeContentOffset = SIDE_NAV_WIDTH_MD;
    } else if (sideNavBreakpoint) {
        homeContentOffset = SIDE_NAV_WIDTH_SM;
    }

    return (
        <Tabs
            screenOptions={{
                headerShown: false,
                tabBarLabelStyle: {
                    fontSize: 12,
                    fontWeight: '500',
                },
                tabBarStyle: {
                    backgroundColor: getColor('$white'),
                    height: 44 + (insets.bottom === 0 ? 10 : insets.bottom),
                    paddingHorizontal: 8,
                },
                tabBarItemStyle: isWeb
                    ? { paddingVertical: 15 }
                    : { paddingTop: insets.bottom === 0 ? 0 : 8 },
                tabBarActiveTintColor: getColor('$black'),
                tabBarInactiveTintColor: getColor('$gray800'),
                sceneStyle: {
                    backgroundColor: getColor('$white'),
                    marginLeft: homeContentOffset,
                },
            }}
            tabBar={SideNavHandler(sideNavBreakpoint)}
        >
            {children}
        </Tabs>
    );
};

export default function TabsNavigation() {
    const { disableRoadmapFeatures } = useRoadmapFeature();

    const { value: enableImTab } = useFeatureGate(FeatureFlags.ENABLE_IM_TAB);
    // Same label for money_map (FF off) and money_system (FF on) so the tab
    // name does not flash when Statsig settles. Only very-legacy Goals differs.
    const roadmapTitle = disableRoadmapFeatures ? 'Goals' : 'Money System';

    return disableRoadmapFeatures ? (
        <TabsComponent>
            {/* This is the original tab order, before Money Map existed: Home, Banking, Guidance, Goals (via Roadmap tab) */}
            <Tabs.Screen
                {...homeTabProps}
                options={{
                    ...homeTabProps.options,
                }}
            />
            <Tabs.Screen
                {...bankingTabProps}
                options={{ ...bankingTabProps.options }}
            />
            <Tabs.Screen
                {...imTabProps(!enableImTab)}
                options={{
                    ...imTabProps(!enableImTab).options,
                }}
            />
            <Tabs.Screen
                {...guidanceTabProps}
                options={{ ...guidanceTabProps.options }}
            />
            <Tabs.Screen
                {...roadmapTabProps(disableRoadmapFeatures, roadmapTitle)}
                options={{
                    ...roadmapTabProps(disableRoadmapFeatures, roadmapTitle)
                        .options,
                }}
            />
        </TabsComponent>
    ) : (
        <TabsComponent>
            {/* This is the new tab order, after Money Map was added: Home, Banking, Roadmap, Guidance */}
            <Tabs.Screen
                {...homeTabProps}
                options={{
                    ...homeTabProps.options,
                }}
            />
            <Tabs.Screen
                {...bankingTabProps}
                options={{ ...bankingTabProps.options }}
            />
            <Tabs.Screen
                {...imTabProps(!enableImTab)}
                options={{
                    ...imTabProps(!enableImTab).options,
                }}
            />
            <Tabs.Screen
                {...roadmapTabProps(disableRoadmapFeatures, roadmapTitle)}
                options={{
                    ...roadmapTabProps(disableRoadmapFeatures, roadmapTitle)
                        .options,
                }}
            />
            <Tabs.Screen
                {...guidanceTabProps}
                options={{ ...guidanceTabProps.options }}
            />
        </TabsComponent>
    );
}
